import React, { useEffect, useState } from 'react';
import { apiCall } from '../../utils/api';
import { toast } from '../ui/sonner';
import { CalendarClock, Loader2 } from 'lucide-react';

const OPTIONS = [
  { id: 'instant', label: 'Instant', desc: 'Funds settle the moment payment clears' },
  { id: 'daily', label: 'Daily', desc: 'Batched nightly at 00:00 UTC' },
  { id: 'weekly', label: 'Weekly', desc: 'Batched every Monday 00:00 UTC' },
];

export default function SettlementScheduleCard() {
  const [schedule, setSchedule] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadForecast = async () => {
    try { setForecast(await apiCall('/api/org/payout-forecast')); } catch {}
  };

  useEffect(() => { (async () => {
    try {
      const r = await apiCall('/api/org/settlement-schedule');
      setSchedule(r.schedule || 'instant');
    } catch { setSchedule('instant'); }
    loadForecast();
  })(); }, []);

  const save = async (id) => {
    const prev = schedule;
    setSchedule(id);
    setSaving(true);
    try {
      await apiCall('/api/org/settlement-schedule', { method: 'PUT', body: JSON.stringify({ schedule: id }) });
      toast.success(`Payout schedule set to ${id}`);
      loadForecast();
    } catch (e) {
      setSchedule(prev);
      toast.error('Could not update schedule', { description: e.message });
    }
    setSaving(false);
  };

  if (schedule === null) return null;
  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4" data-testid="settlement-schedule-card">
      <div className="flex items-center gap-2 mb-3">
        <CalendarClock className="w-4 h-4 text-indigo-600" />
        <h3 className="font-bold text-slate-900 text-sm">Payout schedule</h3>
        {saving && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {OPTIONS.map((o) => (
          <button
            key={o.id}
            onClick={() => save(o.id)}
            disabled={saving}
            className={`text-left px-3 py-2.5 rounded-xl border transition-colors ${schedule === o.id ? 'border-indigo-500 bg-indigo-50 ring-1 ring-indigo-300' : 'border-slate-200 hover:bg-slate-50'}`}
            data-testid={`schedule-option-${o.id}`}
          >
            <p className="text-sm font-bold text-slate-900">{o.label}</p>
            <p className="text-[11px] text-slate-500">{o.desc}</p>
          </button>
        ))}
      </div>
      {forecast && forecast.schedule !== 'instant' && (
        <div className="mt-3 px-3 py-2.5 rounded-xl bg-indigo-50 border border-indigo-100" data-testid="payout-forecast">
          <p className="text-xs font-bold text-indigo-800">
            Next batch: {forecast.next_batch_at ? new Date(forecast.next_batch_at).toLocaleString() : '—'}
          </p>
          <p className="text-[11px] text-indigo-600" data-testid="payout-forecast-pending">
            {forecast.pending.count > 0
              ? `$${Number(forecast.pending.total).toFixed(2)} across ${forecast.pending.count} payment(s) queued`
              : 'No payments queued yet — new payments will settle in this batch'}
          </p>
        </div>
      )}
      <p className="text-[11px] text-slate-400 mt-2">Daily/weekly payments are fraud-screened immediately, then settle in the next batch. Admins can release a batch early.</p>
    </div>
  );
}
